import React, { useState, useMemo, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import { useI18n } from "Hooks";
import { useDispatch } from "Hooks/useDispatch";
import { DataGrid } from "@toddle-edu/ds-data-grid";
import { Modal, Button, Dialog } from "@toddle-edu/ds-web";
import { AddOutlined } from "@toddle-edu/ds-icons";
import { getAIResponse } from "modules/Services";
import { getSelectedCurriculumId } from "modules/CommonSelectors";
import { store } from "store/createStore";

import {
  parseTimestamp,
  secondsToTimestamp as formatTimestamp,
} from "./timestampUtils";
import classes from "./AITutorVideoModality.scss";

const EDITABLE_FIELDS = ["timestamp", "learningMoment", "reflectiveQuestion"];

const sortByTimestamp = checkpoints =>
  [...checkpoints].sort((a, b) => {
    const timeA = parseTimestamp(a.timestamp) ?? Infinity;
    const timeB = parseTimestamp(b.timestamp) ?? Infinity;
    return timeA - timeB;
  });

const MIN_CHECKPOINT_GAP = 2; // seconds

const isTooCloseToExisting = (seconds, checkpoints, skipIndex = -1) =>
  _.some(checkpoints, (cp, i) => {
    if (i === skipIndex) return false;
    const existing = parseTimestamp(cp.timestamp);
    if (existing == null) return false;
    return Math.abs(seconds - existing) < MIN_CHECKPOINT_GAP;
  });

let nextId = 1;
const generateId = () => `cp-${nextId++}`;

const EditLearningMomentsModal = ({
  checkpoints = [],
  url,
  attachmentId,
  updateAttachment,
  setIsPendingAttachmentUpdate = _.noop,
  setToastMsg = _.noop,
  onClose,
  videoDuration = 0,
  renderVideoPlayer,
  conversation,
  assessmentId,
  courseId,
  gradeId,
}) => {
  const { t } = useI18n();
  const dispatch = useDispatch();

  const [editedCheckpoints, setEditedCheckpoints] = useState(() =>
    sortByTimestamp(
      _.map(checkpoints, cp => ({
        _id: generateId(),
        timestamp: cp?.timestamp || "",
        learningMoment: cp?.learningMoment || "",
        reflectiveQuestion: cp?.reflectiveQuestion || "",
      }))
    )
  );
  const [isSaving, setIsSaving] = useState(false);
  const [generatingRows, setGeneratingRows] = useState({});
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const initialCheckpointsRef = useRef(editedCheckpoints);

  const isDirty = useMemo(
    () => !_.isEqual(editedCheckpoints, initialCheckpointsRef.current),
    [editedCheckpoints]
  );

  const handleClose = useCallback(() => {
    if (isDirty) {
      setShowDiscardDialog(true);
      return;
    }
    onClose();
  }, [isDirty, onClose]);

  const handleAIGenerate = useCallback(
    async (index, cpId) => {
      const checkpoint = _.find(editedCheckpoints, { _id: cpId });
      if (!checkpoint) return;

      if (_.isEmpty(_.trim(checkpoint.timestamp))) {
        setToastMsg("ai:timestamp_required_for_generation");
        return;
      }

      setGeneratingRows(prev => ({ ...prev, [cpId]: true }));

      try {
        const checkpointsContext = JSON.stringify(
          _.map(editedCheckpoints, cp => _.omit(cp, "_id"))
        );

        const state = store.getState();
        const curriculumProgramId = getSelectedCurriculumId(state);
        const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");
        const resolvedCourseId =
          courseId ||
          _.get(state, "teacher.selected_class.selected_course", "");
        const resolvedGradeId =
          gradeId ||
          _.get(state, "teacher.selected_class.selected_grades.0", "");

        const response = await dispatch(
          getAIResponse({
            useStreamParserV2: true,
            variables: {
              input: {
                type: "LEARNING_MOMENT_GENERATION",
                entityId: assessmentId,
                entityType: "ai_tutor",
                curriculumProgramId,
                context: {
                  currentVideoCheckpoints: checkpointsContext,
                  inputText: checkpoint.timestamp,
                },
                metadata: {
                  assessmentId,
                  conversationId: conversation?.id,
                  courseId: resolvedCourseId,
                  gradeId: resolvedGradeId,
                  unitPlanId,
                  genericTagIds: [],
                  skipSemanticSearch: true,
                },
              },
            },
          })
        );

        const rawContent = _.get(response, "content", "");
        const jsonString = rawContent
          .replace(/^```json\s*/, "")
          .replace(/```\s*$/, "")
          .trim();
        let parsed;
        try {
          parsed = JSON.parse(jsonString);
        } catch {
          parsed = null;
        }

        if (parsed?.learningMoment && parsed?.reflectiveQuestion) {
          setEditedCheckpoints(prev =>
            _.map(prev, cp => {
              if (cp._id !== cpId) return cp;
              return {
                ...cp,
                learningMoment: parsed.learningMoment,
                reflectiveQuestion: parsed.reflectiveQuestion,
              };
            })
          );
        } else {
          setToastMsg("toastMsgs:something_went_wrong");
        }
      } catch {
        setToastMsg("toastMsgs:something_went_wrong");
      } finally {
        setGeneratingRows(prev => _.omit(prev, cpId));
      }
    },
    [
      editedCheckpoints,
      dispatch,
      setToastMsg,
      assessmentId,
      conversation,
      attachmentId,
      courseId,
      gradeId,
    ]
  );

  const headers = useMemo(
    () => [
      { id: "timestamp", title: t("ai:timestamp"), width: 98 },
      {
        id: "learningMoment",
        title: t("ai:learning_moment_label"),
        width: 160,
      },
      {
        id: "reflectiveQuestion",
        title: t("ai:reflective_question_label"),
        width: 160,
      },
      { id: "aiGenerate", title: "", width: 52 },
      { id: "actions", title: "", width: 52 },
    ],
    [t]
  );

  const handleDeleteRow = useCallback(cpId => {
    setEditedCheckpoints(prev => {
      const index = _.findIndex(prev, cp => cp._id === cpId);
      if (index === -1) return prev;
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
  }, []);

  const tableData = useMemo(
    () =>
      _.map(editedCheckpoints, (cp, index) => {
        const isGenerating = !!generatingRows[cp._id];
        return {
          rowId: cp._id,
          columns: [
            {
              cellType: "text",
              value: cp.timestamp,
              isEditable: !isGenerating,
              placeholder: "0:00",
              styles: { align: "left", padding: "4 8" },
            },
            isGenerating
              ? { cellType: "skeleton", isEditable: false }
              : {
                  cellType: "text",
                  value: cp.learningMoment,
                  isEditable: true,
                  placeholder: t("ai:learning_moment_label"),
                  tooltip: cp.learningMoment || "",
                  styles: { align: "left", padding: "4 8" },
                },
            isGenerating
              ? { cellType: "skeleton", isEditable: false }
              : {
                  cellType: "text",
                  value: cp.reflectiveQuestion,
                  isEditable: true,
                  placeholder: t("ai:reflective_question_label"),
                  tooltip: cp.reflectiveQuestion || "",
                  styles: { align: "left", padding: "4 8" },
                },
            {
              cellType: "icon",
              isEditable: false,
              value: "StarsTwoFilled",
              onIconClick: () => handleAIGenerate(index, cp._id),
            },
            {
              cellType: "icon",
              isEditable: false,
              value: "DeleteOutlined",
              onIconClick: () => handleDeleteRow(cp._id),
            },
          ],
        };
      }),
    [editedCheckpoints, t, handleDeleteRow, handleAIGenerate, generatingRows]
  );

  const handleCellEdit = useCallback(
    params => {
      if (_.isEmpty(params)) return;
      let showTooCloseToast = false;
      setEditedCheckpoints(prev => {
        const updated = [...prev];
        _.forEach(params, editedCell => {
          const { cellCoods, newValue } = editedCell;
          const rowId = cellCoods?.rowId;
          const colId = cellCoods?.colId;
          const value = _.get(newValue, "value", "");

          const rowIndex = _.findIndex(updated, cp => cp._id === rowId);
          if (rowIndex === -1) return;
          if (!_.includes(EDITABLE_FIELDS, colId)) return;

          let finalValue = value;
          if (colId === "timestamp") {
            if (!value || !String(value).trim()) {
              finalValue = "";
            } else {
              const seconds = parseTimestamp(value);
              if (seconds == null) return;
              const clamped =
                videoDuration > 0 ? Math.min(seconds, videoDuration) : seconds;
              if (isTooCloseToExisting(clamped, updated, rowIndex)) {
                showTooCloseToast = true;
                return;
              }
              finalValue = formatTimestamp(clamped);
            }
          }

          updated[rowIndex] = {
            ...updated[rowIndex],
            [colId]: finalValue,
          };
        });
        return sortByTimestamp(updated);
      });
      if (showTooCloseToast) {
        setToastMsg("ai:checkpoint_too_close");
      }
    },
    [videoDuration, setToastMsg]
  );

  const handleAddRow = useCallback(() => {
    setEditedCheckpoints(prev => [
      ...prev,
      {
        _id: generateId(),
        timestamp: "",
        learningMoment: "",
        reflectiveQuestion: "",
      },
    ]);
  }, []);

  const handleAddCheckpointAtTime = useCallback(
    timestamp => {
      const seconds = parseTimestamp(timestamp);
      if (seconds == null) return;
      let showTooCloseToast = false;
      setEditedCheckpoints(prev => {
        if (isTooCloseToExisting(seconds, prev)) {
          showTooCloseToast = true;
          return prev;
        }
        return sortByTimestamp([
          ...prev,
          {
            _id: generateId(),
            timestamp,
            learningMoment: "",
            reflectiveQuestion: "",
          },
        ]);
      });
      if (showTooCloseToast) {
        setToastMsg("ai:checkpoint_too_close");
      }
    },
    [setToastMsg]
  );

  const handleUpdateCheckpointTime = useCallback(
    (checkpointIndex, newTimestamp) => {
      let showTooCloseToast = false;
      setEditedCheckpoints(prev => {
        if (checkpointIndex < 0 || checkpointIndex >= prev.length) return prev;
        const seconds = parseTimestamp(newTimestamp);
        if (seconds == null) return prev;
        const clamped =
          videoDuration > 0 ? Math.min(seconds, videoDuration) : seconds;
        if (isTooCloseToExisting(clamped, prev, checkpointIndex)) {
          showTooCloseToast = true;
          return prev;
        }
        const updated = [...prev];
        updated[checkpointIndex] = {
          ...updated[checkpointIndex],
          timestamp: formatTimestamp(clamped),
        };
        return sortByTimestamp(updated);
      });
      if (showTooCloseToast) {
        setToastMsg("ai:checkpoint_too_close");
      }
    },
    [videoDuration, setToastMsg]
  );

  const onSave = useCallback(async () => {
    const hasEmptyFields = _.some(
      editedCheckpoints,
      cp =>
        _.isEmpty(_.trim(cp.learningMoment)) ||
        _.isEmpty(_.trim(cp.reflectiveQuestion))
    );

    if (hasEmptyFields) {
      setToastMsg("ai:empty_learning_moment_fields");
      return;
    }

    setIsSaving(true);
    setIsPendingAttachmentUpdate(true);
    try {
      const cleanedCheckpoints = _.map(editedCheckpoints, cp =>
        _.omit(cp, "_id")
      );
      await updateAttachment({
        id: attachmentId,
        metadata: {
          checkPoints: cleanedCheckpoints,
          updatePresetAttachment: true,
        },
      });
      onClose();
    } catch {
      setToastMsg("toastMsgs:something_went_wrong");
    } finally {
      setIsSaving(false);
      setIsPendingAttachmentUpdate(false);
    }
  }, [
    attachmentId,
    editedCheckpoints,
    updateAttachment,
    setIsPendingAttachmentUpdate,
    setToastMsg,
    onClose,
  ]);

  const dataGridHeight = useMemo(() => {
    const rowCount = _.size(editedCheckpoints);
    const headerH = 40;
    const rowH = 44;
    const maxHeight = 500;
    return Math.min(headerH + rowCount * rowH + 2, maxHeight);
  }, [editedCheckpoints]);

  return (
    <Modal
      onClose={handleClose}
      hasOverlay
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc
      width="min(1700px, 95vw)"
      height="min(850px, 90vh)"
      dsVersion="2.0"
    >
      <Modal.Header
        title={t("ai:edit_learning_moment")}
        style={{ marginBottom: "0px" }}
        isClosable
        hasSeparator
      />
      <Modal.Body
        isScrollable={false}
        style={{ overflow: "hidden", padding: 0 }}
      >
        <div className={classes.editModalContent}>
          <div className={classes.editModalLeft}>
            <DataGrid
              headers={headers}
              data={tableData}
              rowHeight={44}
              headerHeight={40}
              dataGridHeight={dataGridHeight}
              dataGridWidth="100%"
              isViewMode={false}
              showHoverState
              rowMarkers="none"
              columnSelectionEnabled={false}
              columnsResizable={false}
              onCellEdit={handleCellEdit}
              blurSelectionOnOutsideClick
              tooltipDelayTime={500}
            />
            <Button
              dsVersion="2.0"
              variant="progressive"
              type="plain"
              size="small"
              icon={<AddOutlined />}
              onClick={handleAddRow}
            >
              {t("ai:add_learning_moment")}
            </Button>
          </div>
          <div className={classes.editModalRight}>
            {renderVideoPlayer({
              checkpoints: editedCheckpoints,
              onAddCheckpointAtTime: handleAddCheckpointAtTime,
              onUpdateCheckpointTime: handleUpdateCheckpointTime,
            })}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer hasSeparator>
        <div className={classes.editModalFooter}>
          <div />
          <div className={classes.editModalFooterActions}>
            <Button
              dsVersion="2.0"
              variant="neutral"
              type="outlined"
              onClick={handleClose}
            >
              {t("common:discard")}
            </Button>
            <Button
              dsVersion="2.0"
              variant="progressive"
              onClick={onSave}
              disabled={isSaving}
            >
              {t("common:save_changes")}
            </Button>
          </div>
        </div>
      </Modal.Footer>
      {showDiscardDialog && (
        <Dialog
          variant="delete-dialog"
          title={t("common:discard_changes")}
          message={t("ai:discard_learning_moments_warning")}
          buttonOneText={t("common:cancel")}
          buttonTwoText={t("common:discard")}
          onButtonOneClick={() => setShowDiscardDialog(false)}
          onButtonTwoClick={onClose}
          onClose={() => setShowDiscardDialog(false)}
          dsVersion="2.0"
        />
      )}
    </Modal>
  );
};

EditLearningMomentsModal.propTypes = {
  checkpoints: PropTypes.array,
  url: PropTypes.string.isRequired,
  attachmentId: PropTypes.string,
  updateAttachment: PropTypes.func.isRequired,
  setIsPendingAttachmentUpdate: PropTypes.func,
  setToastMsg: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  videoDuration: PropTypes.number,
  transcriptUrl: PropTypes.string,
  renderVideoPlayer: PropTypes.func.isRequired,
  conversation: PropTypes.object,
  assessmentId: PropTypes.string,
  courseId: PropTypes.string,
  gradeId: PropTypes.string,
};

EditLearningMomentsModal.displayName = "EditLearningMomentsModal";

export default EditLearningMomentsModal;
